import React from 'react';
import { TransactionApi, CurrencyObject, PaymentAction, PaymentState } from 'common/types';
import { Typography, Button, Theme, Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { getPricingString } from 'common/utils/common';
import Grid from '@mui/material/Grid';
import { getCurrencyObjectFromCurrencyCode } from 'common/utils/currencyUtils';
import { PaymentModifyAction } from '../PaymentsList';
import { getPaymentModificationState } from 'common/utils/payments';
import { useTranslation } from 'services/localization/useTranslation';
import { LocaleHelpers } from 'common/locales/helpers';
import { useHasFeature } from 'hooks';
import { getPaymentStateOrActionTranslation } from 'common/utils/translations';

interface Props {
	singleTransaction?: boolean;
	transaction: TransactionApi;
	transactionLoading: boolean;
	modifyTransaction: (transaction: TransactionApi, action: PaymentModifyAction) => void;
	shopCurrency?: CurrencyObject;
}

const TransactionRow = ({
	singleTransaction,
	transaction,
	transactionLoading,
	modifyTransaction,
	shopCurrency,
}: Props) => {
	const { hasFeature } = useHasFeature();
	const { amount } = transaction;
	const currencyObj = getCurrencyObjectFromCurrencyCode(amount.currency, shopCurrency);
	const classes = useStyles();
	const { t } = useTranslation();

	const getActionButton = (actionName: PaymentAction): JSX.Element | null => {
		switch (actionName) {
			case 'REFUND':
				return (
					<Button
						color="primary"
						variant="outlined"
						onClick={() => modifyTransaction(transaction, 'REFUND')}
						disabled={transactionLoading || !hasFeature('MODIFY_PAYMENTS')}
						className={classes.actionBtn}
						data-qa="Refund--refundButton"
					>
						<Typography variant="caption">{t('common:payment.refund', 'Refund')}</Typography>
					</Button>
				);
			case 'CAPTURE':
				return (
					<Button
						color="secondary"
						variant="text"
						onClick={() => modifyTransaction(transaction, 'CAPTURE')}
						disabled={transactionLoading || !hasFeature('MODIFY_PAYMENTS')}
						className={classes.actionBtn}
					>
						<Typography variant="caption">{t('common:actions.charge')}</Typography>
					</Button>
				);
			case 'CANCEL':
				return (
					<Button
						color="primary"
						variant="outlined"
						onClick={() => modifyTransaction(transaction, 'CANCEL')}
						disabled={transactionLoading}
						className={classes.actionBtn}
					>
						<Typography variant="caption">{t('common:actions.release')}</Typography>
					</Button>
				);
			default:
				return null;
		}
	};

	const getActionGridItems = (modificationState: PaymentState | null, actions: PaymentAction[]) => {
		if (!actions.length && modificationState) {
			return (
				<Grid item>
					<Typography variant="caption" color="textSecondary">
						{getPaymentStateOrActionTranslation(modificationState, t)}
					</Typography>
				</Grid>
			);
		} else {
			return actions.map((action, i) => {
				const actionBtn = getActionButton(action);
				return actionBtn ? (
					<Grid key={i + action} item>
						{actionBtn}
					</Grid>
				) : undefined;
			});
		}
	};

	const renderTransactionActionRow = () => {
		const { modificationState, possibleActions } = getPaymentModificationState(transaction);
		if (!modificationState && !possibleActions.length) {
			return null;
		}
		const actionGridItems = getActionGridItems(modificationState, possibleActions);
		return (
			<Grid container justifyContent="flex-end" spacing={1}>
				{actionGridItems}
			</Grid>
		);
	};

	return (
		<React.Fragment>
			{!singleTransaction && (
				<Grid container justifyContent="space-between" spacing={1}>
					<Grid item>
						<Typography variant="body2" color="textSecondary">
							{LocaleHelpers.admin(t).paymentMethods[transaction.method] ?? transaction.method}
						</Typography>
					</Grid>
					<Grid item>
						<Typography variant="body2" color="textSecondary">
							{getPricingString(amount.value, currencyObj)}
						</Typography>
					</Grid>
				</Grid>
			)}
			<Box mt={1}>{renderTransactionActionRow()}</Box>
		</React.Fragment>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		actionBtn: {
			minWidth: 0,
			minHeight: 0,
		},
	}),
);

export default TransactionRow;
